import { Avatar, AvatarFallback } from '@/shared/components/ui/avatar';
import { Badge } from '@/shared/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { ROUTE_PATHS } from '@/shared/constants/routes';
import {
  canOpenMyProfile,
  getRoleBadgeVariant,
  getRoleDisplayLabel,
} from '@/shared/utils/authRoles';
import { useAuthStore } from '@/stores/authStore';
import { LogOut, Settings } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';

const UserMenu = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuthStore();

  const handleLogout = async () => {
    await logout();
    navigate({ to: '/' });
  };

  /** 설정 모드 진입 — 헤더 프로필에서만. 사이드바가 설정 메뉴로 교체된다 */
  const goToSettings = () => {
    navigate({ to: ROUTE_PATHS.MY_ACCOUNT as never });
  };

  const initials = (user?.username ?? 'U').slice(0, 2).toUpperCase();
  const roleLabel = getRoleDisplayLabel(user?.role);
  const showSettings = canOpenMyProfile(user?.role);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="ruda-user-menu flex shrink-0 cursor-pointer sm:min-w-[120px] items-center gap-2.5 rounded-12 border border-border bg-card px-2.5 py-1.5 justify-between"
        >
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-primary/10 text-xs font-semibold text-primary">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="hidden min-w-0 flex-col gap-0.5 text-left w-full sm:flex items-center">
            <span className="truncate text-sm font-medium leading-tight">
              {user?.name || user?.username || '사용자'}
            </span>
            <Badge
              variant={getRoleBadgeVariant(user?.role)}
              className="h-5 px-1.5 py-0 text-[11px] font-medium leading-none"
            >
              {roleLabel}
            </Badge>
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[240px]">
        <DropdownMenuItem disabled className="text-ruda-text-tertiary">
          {user?.name || user?.username}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {showSettings ? (
          <DropdownMenuItem onClick={goToSettings}>
            <Settings />
            설정
          </DropdownMenuItem>
        ) : null}
        {showSettings ? <DropdownMenuSeparator /> : null}
        <DropdownMenuItem variant="destructive" onClick={handleLogout}>
          <LogOut />
          로그아웃
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
